import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Paper,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Button,
  Modal,
  Tooltip,
  Chip,
  Box,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  getPendingCheques,
  updateChequeStatus,
} from "../../redux/features/cheque/chequeSlice";
import { getBanks } from "../../redux/features/Bank/bankSlice";
import axios from "axios";
import { toast } from "react-toastify";

const ChequeDetails = () => {
  const dispatch = useDispatch();
  const cheques = useSelector((state) => state.cheque.cheques);
  const isLoading = useSelector((state) => state.cheque.isLoading);
  const banks = useSelector((state) => state.bank.banks || []);

  const [todayCheques, setTodayCheques] = useState([]);
  const [upcomingCheques, setUpcomingCheques] = useState([]);
  const [selectedCheques, setSelectedCheques] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [allCheques, setAllCheques] = useState([]);

  // Ref for auto-scrolling to bottom
  const tableContainerRef = useRef(null);

  // CASH OUT MODAL STATE
  const [cashOutModalOpen, setCashOutModalOpen] = useState(false);
  const [cashOutMethod, setCashOutMethod] = useState(""); // "bank" | "cash"
  const [selectedBankId, setSelectedBankId] = useState("");
  const [processingCashOut, setProcessingCashOut] = useState(false);

  // TRANSFER STATE
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedChequeForTransfer, setSelectedChequeForTransfer] = useState(null);
  const [transferTo, setTransferTo] = useState("");
  const [transferToId, setTransferToId] = useState("");
  const [transferDescription, setTransferDescription] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // DELETE STATE
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chequeToDelete, setChequeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // EDIT IMAGE STATE
  const [editImageModal, setEditImageModal] = useState(false);
  const [editingCheque, setEditingCheque] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
// ✅ DATE FILTER STATE
const [filterDate, setFilterDate] = useState("");
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API_URL = `${BACKEND_URL}api/cheques`;

  // ---------- Helpers ----------

  // Direction comes from transactionType, which every cheque is created with:
  //   "buy"  -> money leaves us  (we are paying)
  //   "sale" -> money comes to us (we are being paid)
  // `type` only names the counterparty, so it cannot tell the two apart — a
  // Supplier cheque is "buy" when we pay them and "sale" when they pay us.
  const isPayOutCheque = (row) => {
    const tt = String(row?.transactionType || "").toLowerCase();
    if (tt === "buy") return true;
    if (tt === "sale") return false;

    // Legacy rows saved before transactionType was returned by the API.
    const t = String(row?.type || "").toLowerCase();
    return t === "supplier" || t === "shipper" || t === "product";
  };

  const getChequeBankId = (row) => {
    if (!row) return null;
    return (
      row.bankId ||
      row.bank_id ||
      (row.bank && row.bank._id) ||
      row.bank ||
      null
    );
  };

  // ---------- Effects ----------

  useEffect(() => {
    dispatch(getPendingCheques({ status: "all" }));
    dispatch(getBanks());
  }, [dispatch]);

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const baseUrl = BACKEND_URL.endsWith("/")
          ? BACKEND_URL
          : `${BACKEND_URL}/`;

        const [custResp, suppResp] = await Promise.all([
          axios.get(`${baseUrl}api/customers/allcustomer`, {
            withCredentials: true,
          }),
          axios.get(`${baseUrl}api/suppliers`, {
            withCredentials: true,
          }),
        ]);

        const customersData = Array.isArray(custResp.data)
          ? custResp.data
          : custResp.data?.customers || [];

        const suppliersData = Array.isArray(suppResp.data)
          ? suppResp.data
          : suppResp.data?.suppliers || [];

        setCustomers(customersData);
        setSuppliers(suppliersData);
      } catch (err) {
        console.error("❌ Error fetching entities:", err);
        toast.error(
          err.response?.data?.message || "Failed to load customers/suppliers"
        );
      }
    };
    fetchEntities();
  }, [BACKEND_URL]);

  useEffect(() => {
    if (!Array.isArray(cheques)) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const t = [];
    const u = [];
    const all = [];

    cheques.forEach((c) => {
      const d = new Date(c.chequeDate || c.date || Date.now());
      d.setHours(0, 0, 0, 0);
      const dayDiff = (d.getTime() - today.getTime()) / (1000 * 3600 * 24);

      all.push(c);

      if (c.status === false && !c.cancelled) {
        if (c.transferred && c.transferredCashedOut) {
          return;
        }

        if (dayDiff === 0) t.push(c);
        else if (dayDiff > 0 && dayDiff <= 7) u.push(c);
      }
    });

    setAllCheques(all);
    setTodayCheques(t);
    setUpcomingCheques(u);
  }, [cheques]);

  
// ✅ FILTERED CHEQUES BY DATE (MOVE THIS FIRST!)
const filteredCheques = useMemo(() => {
  if (!filterDate) return allCheques;
  
  const selectedDate = new Date(filterDate);
  selectedDate.setHours(0, 0, 0, 0);
  
  return allCheques.filter((cheque) => {
    const chequeDate = new Date(cheque.chequeDate || cheque.date);
    chequeDate.setHours(0, 0, 0, 0);
    return chequeDate.getTime() === selectedDate.getTime();
  });
}, [allCheques, filterDate]);

// ✅ NOW the useEffect can use filteredCheques
useEffect(() => {
  if (filteredCheques.length > 0 && tableContainerRef.current) {
    setTimeout(() => {
      tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
    }, 100);
  }
}, [filteredCheques]);  // ✅ Fixed dependency

  // ---------- Derived selections ----------

  const selectedChequeRows = useMemo(
    () =>
      selectedCheques
        .map((id) => allCheques.find((c) => c._id === id))
        .filter(Boolean),
    [selectedCheques, allCheques]
  );

  const needBankSelection = useMemo(
    () => selectedChequeRows.some((row) => !getChequeBankId(row)),
    [selectedChequeRows]
  );

  const isAllPayoutWithBank = useMemo(
    () =>
      selectedChequeRows.length > 0 &&
      selectedChequeRows.every(
        (row) => isPayOutCheque(row) && !!getChequeBankId(row)
      ),
    [selectedChequeRows]
  );

  const canSubmit = useMemo(() => {
    if (selectedCheques.length === 0) return false;
    return selectedCheques.every((id) => {
      const c = allCheques.find((x) => x._id === id);
      return c?.status === false && !c?.cancelled && !c?.transferred;
    });
  }, [selectedCheques, allCheques]);

  // ---------- Handlers ----------

  const handleStatusChange = (chequeId, isChecked) => {
    const row = allCheques.find((x) => x._id === chequeId);
    if (row?.status === true || row?.cancelled || row?.transferred) return;
    setSelectedCheques((prev) =>
      isChecked ? [...prev, chequeId] : prev.filter((id) => id !== chequeId)
    );
  };

  const handleCashOutClick = () => {
    if (isAllPayoutWithBank) {
      setCashOutMethod("bank");
    } else {
      setCashOutMethod("");
    }
    setSelectedBankId("");
    setCashOutModalOpen(true);
  };

  const handleCashOutSubmit = async () => {
    if (!cashOutMethod && !isAllPayoutWithBank) {
      toast.error("Please select Bank or Cash");
      return;
    }

    if (cashOutMethod === "bank" && needBankSelection && !selectedBankId) {
      toast.error("Please select a bank for cheques without a saved bank");
      return;
    }

    const methodToUse = isAllPayoutWithBank ? "bank" : cashOutMethod;

    setProcessingCashOut(true);

    try {
      for (const chequeId of selectedCheques) {
        const row = allCheques.find((c) => c._id === chequeId);
        if (!row) continue;

        const amount = Number(row.amount) || 0;
        const description = `Cheque cleared: ${row.name || row.description || ""}`;
        const payOut = isPayOutCheque(row);
        const transactionType = payOut ? "subtract" : "add";
        const cashType = payOut ? "deduct" : "add";

        // Step 1: bank/cash transaction FIRST — if this fails, cheque stays pending (safe)
        if (methodToUse === "bank") {
          const savedBankId = getChequeBankId(row);
          const bankIdToUse = savedBankId || selectedBankId;

          if (!bankIdToUse) {
            throw new Error(`No bank selected for cheque: ${row.name || row._id}`);
          }

          await axios.post(
            `${BACKEND_URL}api/banks/${bankIdToUse}/transaction`,
            { amount, type: transactionType, description },
            { withCredentials: true }
          );
        } else if (methodToUse === "cash") {
          await axios.post(
            `${BACKEND_URL}api/cash/add`,
            { balance: amount, type: cashType, description },
            { withCredentials: true }
          );
        }

        // Step 2: mark cheque as cleared ONLY after bank/cash succeeded
        await dispatch(
          updateChequeStatus({
            id: row._id,
            status: true,
            skipBankProcessing: true,
          })
        ).unwrap();
      }

      await dispatch(getPendingCheques({ status: "all" }));
      await dispatch(getBanks());
      setSelectedCheques([]);
      setCashOutModalOpen(false);
      setCashOutMethod("");
      setSelectedBankId("");

      const payOutCount = selectedCheques.filter((id) => {
        const c = allCheques.find((x) => x._id === id);
        return isPayOutCheque(c);
      }).length;
      const payInCount = selectedCheques.length - payOutCount;

      let message = "Cheques processed: ";
      if (payOutCount > 0) message += `${payOutCount} payment(s) deducted. `;
      if (payInCount > 0) message += `${payInCount} receipt(s) added.`;

      toast.success(message);
    } catch (e) {
      const msg = e?.message || e?.error || "Failed to cash out cheques";
      console.error(msg);
      toast.error(msg);
    } finally {
      setProcessingCashOut(false);
    }
  };

  const handleCloseCashOutModal = () => {
    if (!processingCashOut) {
      setCashOutModalOpen(false);
      setCashOutMethod("");
      setSelectedBankId("");
    }
  };

  const handleCancel = async (chequeId) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this cheque? This will reverse the balance."
      )
    ) {
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/cancel/${chequeId}`,
        {},
        { withCredentials: true }
      );

      await dispatch(getPendingCheques({ status: "all" }));
      toast.success("Cheque cancelled successfully");
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to cancel cheque";
      console.error(msg);
      toast.error(msg);
    }
  };

  const handleCashOutTransferred = async (chequeId) => {
    if (
      !window.confirm(
        "Mark this transferred cheque as cashed out? This will NOT affect your bank balance."
      )
    ) {
      return;
    }

    try {
      await axios.patch(
        `${API_URL}/cashout-transferred/${chequeId}`,
        {},
        { withCredentials: true }
      );

      await dispatch(getPendingCheques({ status: "all" }));
      toast.success("Transferred cheque marked as cashed out successfully");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to cash out transferred cheque";
      console.error(msg);
      toast.error(msg);
    }
  };

  const handleTransferClick = (cheque) => {
    setSelectedChequeForTransfer(cheque);
    setTransferTo("");
    setTransferToId("");
    setTransferDescription("");
    setTransferModalOpen(true);
  };

  const handleTransferSubmit = async () => {
    if (!transferTo || !transferToId) {
      toast.error("Please select transfer destination");
      return;
    }

    setIsTransferring(true);
    try {
      await axios.post(
        `${API_URL}/transfer/${selectedChequeForTransfer._id}`,
        {
          transferTo,
          transferToId,
          description: transferDescription,
        },
        { withCredentials: true }
      );

      await dispatch(getPendingCheques({ status: "all" }));
      setTransferModalOpen(false);
      setTransferTo("");
      setTransferToId("");
      setTransferDescription("");
      setSelectedChequeForTransfer(null);
      toast.success("Cheque transferred successfully");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to transfer cheque";
      console.error(msg);
      toast.error(msg);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
    setTransferTo("");
    setTransferToId("");
    setTransferDescription("");
    setSelectedChequeForTransfer(null);
  };

  const handleCloseModal = () => setSelectedImage(null);

  const handleEditImageClick = (cheque) => {
    setEditingCheque(cheque);
    setNewImageFile(null);
    setEditImageModal(true);
  };

  const handleEditImageSubmit = async () => {
    if (!newImageFile) { toast.error("Please select an image"); return; }
    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", newImageFile);
      await axios.patch(`${API_URL}/${editingCheque._id}/image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await dispatch(getPendingCheques({ status: "all" }));
      toast.success("Image updated successfully");
      setEditImageModal(false);
      setEditingCheque(null);
      setNewImageFile(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update image");
    } finally {
      setIsUploadingImage(false);
    }
  };

  // ✅ NEW: Delete handlers
  const handleDeleteClick = (cheque) => {
    setChequeToDelete(cheque);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setChequeToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!chequeToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/${chequeToDelete._id}`,
        { withCredentials: true }
      );

      await dispatch(getPendingCheques({ status: "all" }));
      toast.success("Cancelled cheque deleted successfully");
      handleCloseDeleteDialog();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to delete cheque";
      console.error(msg);
      toast.error(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------- Render helpers ----------

  const renderStatus = (row) => (
    <>
      {row.cancelled ? (
        <Chip label="Cancelled" color="error" size="small" />
      ) : row.transferredCashedOut ? (
        <Chip label="Cashed Out (Transferred)" color="success" size="small" />
      ) : row.transferred ? (
        <Chip label="Transferred" color="info" size="small" />
      ) : row.status ? (
        <Chip label="Cleared" size="small" />
      ) : (
        <Chip label="Pending" color="warning" size="small" />
      )}
    </>
  );

  const renderSelectCheckbox = (row) => {
    const disabled =
      row.status === true ||
      row.cancelled === true ||
      row.transferred === true;
    const box = (
      <Checkbox
        checked={selectedCheques.includes(row._id)}
        onChange={(e) => handleStatusChange(row._id, e.target.checked)}
        disabled={disabled}
      />
    );
    let tip = "";
    if (row.status === true) tip = "Already cleared";
    if (row.cancelled === true) tip = "Cheque is cancelled";
    if (row.transferred === true) {
      if (row.transferredCashedOut) {
        tip = "Transferred cheque already cashed out by recipient";
      } else {
        tip = "Cheque is transferred - use Cash Out button to mark as cashed";
      }
    }
    return tip ? (
      <Tooltip title={tip}>
        <span>{box}</span>
      </Tooltip>
    ) : (
      box
    );
  };

  const renderActions = (row) => (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {/* ✅ NEW: Delete button for cancelled cheques */}
      {row.cancelled && (
        <Tooltip title="Delete Cancelled Cheque">
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {!row.cancelled && !row.status && (
        <>
          {!row.transferred && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleCancel(row._id)}
            >
              Cancel
            </Button>
          )}

          {!row.transferred && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleTransferClick(row)}
            >
              Transfer
            </Button>
          )}

          {row.transferred && !row.transferredCashedOut && (
            <>
              <Button
                variant="outlined"
                color="success"
                size="small"
                onClick={() => handleCashOutTransferred(row._id)}
              >
                Cash Out
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handleCancel(row._id)}
              >
                Cancel
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );

  if (isLoading) return <CircularProgress />;

  // ---------- JSX ----------

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Cheques (Pending &amp; Cleared)
      </Typography>
 
      <Stack spacing={2} sx={{ mb: 2 }}>
        {todayCheques.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>Cheques Due Today</AlertTitle>
            You have {todayCheques.length} cheque(s) due for cash today:
            <List dense>
              {todayCheques.map((c) => (
                <ListItem key={c._id}>
                  <ListItemText
                    primary={`${c.name} - ${c.type} - Rs ${Number(
                      c.amount || 0
                    ).toLocaleString()}`}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
        {upcomingCheques.length > 0 && (
          <Alert severity="info">
            <AlertTitle>Upcoming Cheques</AlertTitle>
            You have {upcomingCheques.length} cheque(s) coming up in the next 7
            days.
          </Alert>
        )}
   </Stack>

      {/* ✅ DATE FILTER */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
          label="Filter by Date"
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
        {filterDate && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setFilterDate("")}
          >
            Clear Filter
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          Showing: {filteredCheques.length} of {allCheques.length} cheques
        </Typography>
      </Box>

      <Paper>
        <TableContainer
          ref={tableContainerRef}
          sx={{
            maxHeight: 350,
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>User Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Select</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>View</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: '#1976d2', color: 'white' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {filteredCheques.map((row, index) => (
                <TableRow key={row._id || index} hover>
                  <TableCell>
                    {new Date(row.chequeDate || row.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.userName || "—"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.name}
                  </TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>Rs {Number(row.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>{renderStatus(row)}</TableCell>
                  <TableCell>{renderSelectCheckbox(row)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {row.chequeImage?.filePath ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedImage(row.chequeImage.filePath)}
                        >
                          View
                        </Button>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No Image
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        onClick={() => handleEditImageClick(row)}
                      >
                        Edit
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>{renderActions(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Tooltip
          title={
            selectedCheques.length === 0
              ? "Select at least one cheque"
              : !canSubmit
              ? "Only pending cheques can be cashed"
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCashOutClick}
              disabled={!canSubmit}
              sx={{ mt: 2, ml: 2, mb: 2 }}
            >
              Cash Out Selected Cheques ({selectedCheques.length})
            </Button>
          </span>
        </Tooltip>
      </Paper>

      {/* CASH OUT MODAL */}
      <Modal open={cashOutModalOpen} onClose={handleCloseCashOutModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Cash Out Cheques
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            {(() => {
              const payOutCount = selectedCheques.filter((id) => {
                const c = allCheques.find((x) => x._id === id);
                return isPayOutCheque(c);
              }).length;
              const payInCount = selectedCheques.length - payOutCount;

              const payOutTotal = selectedCheques
                .filter((id) => {
                  const c = allCheques.find((x) => x._id === id);
                  return isPayOutCheque(c);
                })
                .reduce(
                  (sum, id) =>
                    sum +
                    Number(allCheques.find((x) => x._id === id)?.amount || 0),
                  0
                );

              const payInTotal = selectedCheques
                .filter((id) => {
                  const c = allCheques.find((x) => x._id === id);
                  return !isPayOutCheque(c);
                })
                .reduce(
                  (sum, id) =>
                    sum +
                    Number(allCheques.find((x) => x._id === id)?.amount || 0),
                  0
                );

              return (
                <>
                  {payOutCount > 0 && (
                    <Typography variant="body2" color="error.main">
                      💸 {payOutCount} payment(s) will DEDUCT Rs{" "}
                      {payOutTotal.toLocaleString()}
                    </Typography>
                  )}
                  {payInCount > 0 && (
                    <Typography variant="body2" color="success.main">
                      💰 {payInCount} receipt(s) will ADD Rs{" "}
                      {payInTotal.toLocaleString()}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontWeight: "bold" }}
                  >
                    Net: Rs {(payInTotal - payOutTotal).toLocaleString()}
                  </Typography>
                </>
              );
            })()}
          </Alert>

          {!isAllPayoutWithBank && (
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend">Select Destination</FormLabel>
              <RadioGroup
                value={cashOutMethod}
                onChange={(e) => {
                  setCashOutMethod(e.target.value);
                  if (e.target.value === "cash") {
                    setSelectedBankId("");
                  }
                }}
              >
                <FormControlLabel
                  value="bank"
                  control={<Radio />}
                  label="Bank Account"
                  disabled={processingCashOut}
                />
                <FormControlLabel
                  value="cash"
                  control={<Radio />}
                  label="Cash"
                  disabled={processingCashOut}
                />
              </RadioGroup>
            </FormControl>
          )}

          {cashOutMethod === "bank" && needBankSelection && (
            <TextField
              select
              label="Select Bank (for cheques without a saved bank)"
              value={selectedBankId}
              onChange={(e) => setSelectedBankId(e.target.value)}
              fullWidth
              margin="normal"
              disabled={processingCashOut}
            >
              <MenuItem value="">-- Select Bank --</MenuItem>
              {banks.map((bank) => (
                <MenuItem key={bank._id} value={bank._id}>
                  {bank.bankName} - Rs{" "}
                  {Number(
                    bank.balance || bank.totalBalance || 0
                  ).toLocaleString()}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCashOutSubmit}
              fullWidth
              disabled={
                processingCashOut ||
                (!cashOutMethod && !isAllPayoutWithBank) ||
                (cashOutMethod === "bank" &&
                  needBankSelection &&
                  !selectedBankId)
              }
            >
              {processingCashOut ? "Processing..." : "Confirm Cash Out"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseCashOutModal}
              fullWidth
              disabled={processingCashOut}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Image Modal */}
      <Modal open={Boolean(selectedImage)} onClose={handleCloseModal}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            position: "relative",
          }}
        >
          <img
            src={selectedImage}
            alt="Cheque"
            style={{ maxWidth: "90%", maxHeight: "90%" }}
          />
          <Button
            onClick={handleCloseModal}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              zIndex: 1,
              backgroundColor: "black",
              color: "white",
            }}
          >
            Close
          </Button>
        </div>
      </Modal>

      {/* TRANSFER MODAL */}
      <Modal open={transferModalOpen} onClose={handleCloseTransferModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Transfer Cheque
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Cheque Details:</strong>
            <br />
            Amount: Rs{" "}
            {Number(selectedChequeForTransfer?.amount || 0).toLocaleString()}
            <br />
            From: {selectedChequeForTransfer?.name}
          </Typography>

          <TextField
            select
            label="Transfer To"
            value={transferTo}
            onChange={(e) => {
              setTransferTo(e.target.value);
              setTransferToId("");
            }}
            fullWidth
            margin="normal"
          >
            <MenuItem value="">-- Select Type --</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="supplier">Supplier</MenuItem>
          </TextField>

          {transferTo && (
            <TextField
              select
              label={`Select ${
                transferTo === "customer" ? "Customer" : "Supplier"
              }`}
              value={transferToId}
              onChange={(e) => setTransferToId(e.target.value)}
              fullWidth
              margin="normal"
            >
              <MenuItem value="">
                -- Select {transferTo === "customer" ? "Customer" : "Supplier"} --
              </MenuItem>
              {(transferTo === "customer" ? customers : suppliers).map(
                (entity) => (
                  <MenuItem key={entity._id} value={entity._id}>
                    {entity.username || entity.name}
                  </MenuItem>
                )
              )}
            </TextField>
          )}

          <TextField
            label="Description (Optional)"
            value={transferDescription}
            onChange={(e) => setTransferDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g., Payment for invoice #123"
          />

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTransferSubmit}
              fullWidth
              disabled={!transferTo || !transferToId || isTransferring}
            >
              {isTransferring ? "Transferring..." : "Transfer"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseTransferModal}
              fullWidth
              disabled={isTransferring}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* EDIT IMAGE MODAL */}
      <Modal open={editImageModal} onClose={() => !isUploadingImage && setEditImageModal(false)}>
        <Box sx={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 380, bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 2,
        }}>
          <Typography variant="h6" gutterBottom>Update Cheque Image</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editingCheque?.name} — Rs {Number(editingCheque?.amount || 0).toLocaleString()}
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setNewImageFile(e.target.files[0])}
            style={{ marginBottom: 16 }}
          />
          {newImageFile && (
            <img
              src={URL.createObjectURL(newImageFile)}
              alt="preview"
              style={{ width: "100%", maxHeight: 200, objectFit: "contain", marginBottom: 16 }}
            />
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained" fullWidth
              onClick={handleEditImageSubmit}
              disabled={isUploadingImage || !newImageFile}
            >
              {isUploadingImage ? "Uploading..." : "Save Image"}
            </Button>
            <Button
              variant="outlined" fullWidth
              onClick={() => setEditImageModal(false)}
              disabled={isUploadingImage}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: '#d32f2f' }}>
          🗑️ Delete Cancelled Cheque
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this cancelled cheque?
            <br /><br />
            <strong>Amount:</strong> Rs {Number(chequeToDelete?.amount || 0).toLocaleString()}
            <br />
            <strong>Type:</strong> {chequeToDelete?.type || "N/A"}
            <br />
            <strong>Description:</strong> {chequeToDelete?.name || chequeToDelete?.description || "N/A"}
            <br />
            <strong>Date:</strong> {chequeToDelete?.chequeDate || chequeToDelete?.date 
              ? new Date(chequeToDelete.chequeDate || chequeToDelete.date).toLocaleDateString() 
              : "N/A"}
            <br /><br />
            <span style={{ color: '#1976d2' }}>
              A history record will be created before deletion for audit purposes.
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            variant="outlined"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {isDeleting ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ChequeDetails;